import React from 'react'
import { Box, Button, InputGroup, Text } from '@clear/design-system'
import { useForm } from 'react-hook-form'
import { Form, StepSubTitle, StepTitle } from '@components/layout/Form'
import { verificationCodeSchema } from '@validators/schema/enroll/authSchema'
import { yupResolver } from '@hookform/resolvers/yup'

type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

interface VerificationCodeFormProps {
  formData: {
    email?: string
    phoneNumber?: string
    otpSessionId?: string
  }
  otpFailureMessage?: string
  isLoading?: boolean
  onSubmit: (formData: VerificationCodeFormProps) => void
  onSubmitContact?: (formData: InitiateAuth) => void
  verificationFormData?: InitiateAuth
}

const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  formData,
  onSubmit,
  otpFailureMessage,
  isLoading,
  onSubmitContact,
  verificationFormData,
}) => {
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(verificationCodeSchema),
    mode: 'onChange',
  })
  const { errors, isValid } = formState

  async function onFormSubmit(formData: any) {
    onSubmit(formData)
  }

  const SemiBoldText = ({ children }) => {
    return <Text fontWeight="600">{children}</Text>
  }
  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@')
    if (local.length <= 3) {
      return (
        <SemiBoldText>
          {local[0]}***@{domain}
        </SemiBoldText>
      )
    }
    return (
      <SemiBoldText>
        {local[0]}
        {'*'.repeat(local.length - 3)}
        {local.slice(-2)}@{domain}
      </SemiBoldText>
    )
  }

  const maskPhoneNumber = (phoneNumber: string) => {
    const lastFour = phoneNumber.slice(-4)
    return <SemiBoldText>(***) ***-{lastFour}</SemiBoldText>
  }

  const renderText = () => {
    const { email, phoneNumber } = formData || {}
    const maskedEmail = email ? maskEmail(email) : null
    const maskedPhone = phoneNumber ? maskPhoneNumber(phoneNumber) : null

    if (maskedEmail && maskedPhone) {
      return (
        <>
          We sent a temporary code to {maskedEmail} and {maskedPhone}. Please
          enter one of them below.
        </>
      )
    }

    if (maskedEmail) {
      return (
        <>We sent a temporary code to {maskedEmail}. Please enter it below.</>
      )
    }

    if (maskedPhone) {
      return (
        <>We sent a temporary code to {maskedPhone}. Please enter it below.</>
      )
    }

    return 'We sent a temporary code to your registered contact. Please enter it below.'
  }

  const handleResendClick = () => {
    onSubmitContact(verificationFormData)
  }

  return (
    <Box p={3}>
      <StepTitle mt={0} color="brandDarkBlue" fontWeight="bold">
        Enter Verification Code
      </StepTitle>

      <StepSubTitle>{renderText()}</StepSubTitle>

      <Form onSubmit={handleSubmit(onFormSubmit)}>
        <InputGroup
          data-cy="verificationCode"
          placeholder={'Verification Code'}
          {...register('verificationCode')}
          autoFocus
          error={errors.verificationCode?.message}
          containerProps={{ padding: 0 }}
        />
        <Text mt={2} color="utilityDarkErrorRed">
          {otpFailureMessage}
        </Text>
        <Button
          mt={5}
          type="submit"
          text="Verify"
          id="authv2-verify-otp-btn"
          variant="primary"
          disabled={!isValid || isLoading}
          color="neutralsWhite"
          data-cy="authv2-verify-otp-btn"
          borderRadius="0rem"
        />
        {onSubmitContact && verificationFormData && (
          <Button
            mt={2}
            text="Resend Code"
            id="authv-resend-otp-btn"
            variant="tertiary"
            disabled={isLoading}
            onClick={handleResendClick}
            data-cy="authv-resend-otp-btn"
            borderRadius="0rem"
            border="none"
          />
        )}
      </Form>
    </Box>
  )
}

export default VerificationCodeForm
